import type { ActorEvent, CustomViewConfig, RecordLikeProtocol, RichTextContentProtocol } from '@lighthouse/core'
import type { ContainerLayoutContextValue, FlowLayoutNode } from '@lighthouse/shared'
import { ContainerLayoutProvider, useContainerLayoutContext, ViewEmptyState } from '@lighthouse/shared'
import type { BreakPointSize } from '@lighthouse/tools'
import React, { forwardRef, memo, useMemo } from 'react'

import { useEffectiveAnchorId, useViewBlockContext } from '../../context'
import { useScrollToAnchorWhenHashMatch } from '../../hooks/useScrollToAnchorWhenHashMatch'
import { MatrixView } from './MatrixView'
import { useMultiDimensionalMatrix } from './useMultiDimensionalMatrix'

interface CustomMultiDimensionalBlockProps {
    node: FlowLayoutNode
    breakPoint?: BreakPointSize
    readonly?: boolean
    onRecordClick?: (id: string, actorEvent: ActorEvent, record?: RecordLikeProtocol) => Promise<void>
    onRenderRichText: (v: RichTextContentProtocol) => string
}

export const CustomMultiDimensionalBlock = memo(
    forwardRef<HTMLDivElement, CustomMultiDimensionalBlockProps>((props, ref) => {
        const { node, readonly, onRecordClick, onRenderRichText } = props
        const { blockData, recordPoints, recordPoolAtom, dataSource } = useViewBlockContext()
        const { id, config } = blockData
        const { breakPoint: baseBreakPointConfig, multiDimensionalConfig, anchor } = config as CustomViewConfig
        const { anchorId } = anchor || {}
        const effectiveAnchorId = useEffectiveAnchorId(anchorId)
        useScrollToAnchorWhenHashMatch(effectiveAnchorId, anchor)
        const showEmpty = !dataSource || recordPoints.length === 0
        const { viewLayout, layout, emptyView } = baseBreakPointConfig
        const gap = viewLayout?.gap?.size || 0
        const { id: dsId, schema } = dataSource

        const { level } = useContainerLayoutContext()

        const contextValue: ContainerLayoutContextValue = useMemo(() => {
            return {
                parentId: id,
                level: level + 1,
                size: {
                    width: {
                        size: 'fill'
                    },
                    height: {
                        size: 'auto'
                    }
                },
                layout: {
                    ...layout
                }
            }
        }, [id, level, layout])

        // 构建多维矩阵数据
        const matrixData = useMultiDimensionalMatrix({
            recordPoolAtom,
            recordPoints,
            dsId,
            schema,
            config: {
                rowDimension: multiDimensionalConfig?.rowDimension || '',
                columnDimension: multiDimensionalConfig?.columnDimension || '',
                rowDimensionSortField: multiDimensionalConfig?.rowDimensionSortField,
                columnDimensionSortField: multiDimensionalConfig?.columnDimensionSortField,
                rowDimensionSortMode: multiDimensionalConfig?.rowDimensionSortMode,
                columnDimensionSortMode: multiDimensionalConfig?.columnDimensionSortMode
            }
        })

        if (!multiDimensionalConfig || !multiDimensionalConfig.rowDimension || !multiDimensionalConfig.columnDimension) {
            return null
        }

        if (showEmpty) {
            return <ViewEmptyState id={effectiveAnchorId} config={emptyView} onRenderRichText={onRenderRichText} />
        }

        return (
            <ContainerLayoutProvider value={contextValue}>
                <MatrixView
                    ref={ref}
                    id={effectiveAnchorId}
                    node={node}
                    matrixData={matrixData}
                    gap={gap}
                    readonly={readonly}
                    onRecordClick={onRecordClick}
                />
            </ContainerLayoutProvider>
        )
    })
)
